/**
 * Emailed links that reach the other side of the app.
 *
 * Parent emails used to link to the STAFF pages /invoices/<id> and
 * /statements/<id>; every one sent before the fix still does. And a staff
 * member who is also a family's guardian (same email) gets the parent
 * emails, whose links now open /parent/... pages that refuse staff roles.
 * Rather than landing either on Unauthorized, the route sends each role to
 * its own page for the same record. Role protection is unchanged: the
 * redirect only picks the page, and that page's own guard still decides.
 */

import React from "react";
import { Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import { statementsApi } from "@/api/statements";
import { AuthLoading } from "./ProtectedRoute";

const isParent = (role: string) => role === "parent";
const isNotParent = (role: string) => role !== "parent";

/** `?month=` value: YYYY-MM. */
const MONTH_RE = /^20\d{2}-(0[1-9]|1[0-2])$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Renders `redirect` for a signed-in user whose role matches `when`, else
 * `children` (the route's normal guarded page). Signed-out visitors get the
 * children, whose guard sends them to sign in and back here afterwards.
 */
export const RedirectByRole: React.FC<{
  when: (role: string) => boolean;
  redirect: React.ReactElement;
  children: React.ReactElement;
}> = ({ when, redirect, children }) => {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  // A forced password change comes first; the guard handles it and the
  // change-password page brings the user back here.
  if (isHydrated && isAuthenticated && user && !user.must_change_password && when(user.role)) {
    return redirect;
  }
  return children;
};

/** Navigate with replace, keeping the router state (the after-sign-in flag). */
const Go: React.FC<{ to: string }> = ({ to }) => {
  const { state } = useLocation();
  return <Navigate to={to} replace state={state} />;
};

// --- Parent on a staff link -------------------------------------------------

const ParentInvoiceRedirect: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  return <Go to={`/parent/invoices/${encodeURIComponent(id)}`} />;
};

/** /statements/<id>: open the parent page on that statement's month and family. */
const ParentStatementRedirect: React.FC = () => {
  const { statementId = "" } = useParams<{ statementId: string }>();
  const lookup = UUID_RE.test(statementId);
  const { data, isLoading } = useQuery({
    queryKey: ["statements", "email-link", statementId],
    queryFn: async () => (await statementsApi.get(statementId)).data,
    enabled: lookup,
    retry: false,
  });
  if (lookup && isLoading) return <AuthLoading />;
  const month = data?.period_start?.slice(0, 7);
  const params = new URLSearchParams();
  if (month && MONTH_RE.test(month)) params.set("month", month);
  if (data?.family?.id) params.set("family", data.family.id);
  const query = params.toString();
  return <Go to={`/parent/statements${query ? `?${query}` : ""}`} />;
};

export const ParentOnStaffInvoice: React.FC<{ children: React.ReactElement }> = ({ children }) => (
  <RedirectByRole when={isParent} redirect={<ParentInvoiceRedirect />}>
    {children}
  </RedirectByRole>
);

export const ParentOnStaffStatement: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => (
  <RedirectByRole when={isParent} redirect={<ParentStatementRedirect />}>
    {children}
  </RedirectByRole>
);

export const ParentOnStaffStatements: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => (
  <RedirectByRole when={isParent} redirect={<Go to="/parent/statements" />}>
    {children}
  </RedirectByRole>
);

// --- Staff on a parent link -------------------------------------------------

const StaffInvoiceRedirect: React.FC = () => {
  const { invoiceId = "" } = useParams<{ invoiceId: string }>();
  return <Go to={`/invoices/${encodeURIComponent(invoiceId)}`} />;
};

/**
 * /parent/statements?month=&family=: the staff statement for that family and
 * month when there is one, else the staff list on that month.
 */
const StaffStatementsRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get("month");
  const familyParam = searchParams.get("family");
  const month = monthParam && MONTH_RE.test(monthParam) ? monthParam : null;
  const family = familyParam && UUID_RE.test(familyParam) ? familyParam : null;
  const lookup = !!month && !!family;
  const { data, isLoading } = useQuery({
    queryKey: ["statements", "email-link", family, month],
    queryFn: async () =>
      (
        await statementsApi.list({
          family_id: family ?? undefined,
          statement_month: month ?? undefined,
          limit: 1,
        })
      ).data,
    enabled: lookup,
    retry: false,
  });
  if (lookup && isLoading) return <AuthLoading />;
  const found = data?.items?.find((s) => s.family_id === family);
  if (found) return <Go to={`/statements/${encodeURIComponent(found.statement_id)}`} />;
  return <Go to={month ? `/statements?period=${month}` : "/statements"} />;
};

export const StaffOnParentInvoice: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => (
  <RedirectByRole when={isNotParent} redirect={<StaffInvoiceRedirect />}>
    {children}
  </RedirectByRole>
);

export const StaffOnParentStatements: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => (
  <RedirectByRole when={isNotParent} redirect={<StaffStatementsRedirect />}>
    {children}
  </RedirectByRole>
);
